import ApplicationModel, {
  type ApplicationModelConstructor,
} from 'components/framework/ApplicationModel';
import Fragment from 'components/framework/Fragment';
import ExtensionField from '../ExtensionField';

function filterType(model: ApplicationModel): string {
  const field = model.modelConstructor.fields['extensionFields'];
  if (!field)
    throw new Error(`Model ${this.typename} does not define extensionFields`);
  return field.args.filters;
}

async function getExtensionFields(
  model: ApplicationModel & { id: string },
  filters: Record<string, any>
) {
  return ApplicationModel.query<{
    record: { extensionFields: { name: string; value: string }[] };
  }>(
    new Fragment(model.typename.toLowerCase())
      .argument('id', model.id, 'ID!')
      .attr('id')
      .alias('record')
      .fragment(
        new Fragment('extensionFields')
          .argument('filters', filters, filterType(model))
          .attr('name', 'value')
      )
  );
}

export interface HasExtensionFields {
  getExtensionField<T>(
    extensionIdentifier: string,
    fieldName: string
  ): Promise<T | null>;

  getExtensionFields(extensionIdentifier: string): Promise<any>;

  setExtensionField(
    extensionIdentifier: string,
    fieldName: string,
    fieldValue: any
  ): Promise<any>;

  setExtensionFields(
    extensionIdentifier: string,
    fields: Record<string, any>
  ): Promise<any>;

  clearExtensionField(
    extensionIdentifier: string,
    fieldName: string
  ): Promise<any>;

  clearExtensionFields(
    extensionIdentifier: string,
    fieldNames: string[]
  ): Promise<any>;

  searchExtensionFields(
    extensionIdentifier: string,
    names: string[],
    key: string,
    query: string
  ): Promise<ExtensionField[]>;
}

export function HasExtensionFields(
  originalClass: ApplicationModelConstructor<ApplicationModel & { id: string }>
): any {
  return class extends originalClass implements HasExtensionFields {
    /**
     * Get an extension field value by name
     */
    async getExtensionField<T>(extensionIdentifier: string, fieldName: string) {
      const result = await getExtensionFields(this, {
        extensionIdentifier,
        name: fieldName,
      });
      const extensionFields = result.record.extensionFields;

      if (extensionFields.length > 0) {
        return extensionFields[0].value as T;
      } else {
        return null;
      }
    }

    /**
     * Get all extension fields for an extension on this record
     */
    async getExtensionFields(extensionIdentifier: string) {
      const result = await getExtensionFields(this, {
        extensionIdentifier,
      });

      return result.record.extensionFields;
    }

    /**
     * Set an extension field value
     */
    async setExtensionField(
      extensionIdentifier: string,
      fieldName: string,
      fieldValue: any
    ) {
      return ApplicationModel.mutate(
        new Fragment('createExtensionField').argument(
          'attributes',
          {
            extension: { id: extensionIdentifier },
            extensionFieldableId: this.id,
            extensionFieldableType: this.typename,
            name: fieldName,
            value: fieldValue,
          },
          ExtensionField.inputType
        )
      );
    }

    /**
     * Set multiple extension field values
     */
    async setExtensionFields(
      extensionIdentifier: string,
      fields: Record<string, any>
    ) {
      const fieldsArray = Object.entries(fields).map(([name, value]) => ({
        name,
        value,
      }));

      return ApplicationModel.mutate(
        new Fragment('createExtensionFields').argument(
          'attributes',
          {
            extension: { id: extensionIdentifier },
            extensionFieldableId: this.id,
            extensionFieldableType: this.typename,
            fields: fieldsArray,
          },
          'ExtensionFieldsAttributes!'
        )
      );
    }

    /**
     * Clear the value from an extension field
     */
    async clearExtensionField(extensionIdentifier: string, fieldName: string) {
      return ApplicationModel.mutate(
        new Fragment('deleteExtensionField')
          .argument('extensionIdentifier', extensionIdentifier, 'String')
          .argument('extensionFieldableType', this.typename, 'String')
          .argument('extensionFieldableId', this.id, 'ID')
          .argument('name', fieldName, '[String!]!')
          .attr('success')
      );
    }

    /**
     * Clear all supplied extension fields
     */
    async clearExtensionFields(
      extensionIdentifier: string,
      fieldNames: string[]
    ) {
      return ApplicationModel.mutate(
        new Fragment('deleteExtensionField')
          .argument('extensionIdentifier', extensionIdentifier, 'String')
          .argument('extensionFieldableType', this.typename, 'String')
          .argument('extensionFieldableId', this.id, 'ID')
          .argument('name', fieldNames, '[String!]!')
          .attr('success')
      );
    }

    /**
     * Search extension fields scoped by name for those matching a given query
     */
    async searchExtensionFields(
      extensionIdentifier: string,
      names: string[],
      key: string,
      query: string
    ): Promise<ExtensionField[]> {
      return ExtensionField.select('name', 'value')
        .where({
          names,
          extensionIdentifier,
          key,
          query,
          extensionFieldableType: this.typename.toUpperCase(),
          extensionFieldableId: this.id,
        })
        .per(500)
        .findInBatches();
    }
  };
}
